import React from 'react';
import { Link } from 'react-router-dom';
import styles from './MarketingPage.module.css';
import FinalCTASection from '../components/marketing/sections/FinalCTASection';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';

const WhyVeloxPage = () => {
  const revealRef = useReveal();

  useDocumentMeta({
    title: 'About Velox',
    description:
      'Velox exists because introductions still disappear after the meeting. We built a relationship system so exchanging details is effortless and the people you meet stay useful.',
    path: '/why-velox',
  });

  return (
    <div ref={revealRef}>
      <section className={styles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${styles.pageHeroInner}`}>
          <p className="vxm-eyebrow">About</p>
          <h1 className="vxm-h1">Make every introduction worth meeting again</h1>
          <p className="vxm-lead">
            Effortless to exchange, impossible to lose, and easy to continue.
          </p>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">The problem we kept seeing</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-4)' }}>
              Even in a digital age, people still exchange paper business cards
              that go in a drawer, get lost, or are never acted on. Sharing a
              phone number or connecting on LinkedIn can feel awkward and slow.
              Those new contacts then disappear into a long, undifferentiated
              list. People forget where they met, what they discussed, and why
              the connection mattered. LinkedIn also does not necessarily
              provide the useful contact details or private relationship
              context people actually need afterwards.
            </p>
            <p className="vxm-body">
              Velox was created to make exchanging details effortless, and to
              make sure the relationship does not disappear after the
              introduction.
            </p>
          </div>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">What we built instead</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-4)' }}>
              Velox is a relationship system for people whose work depends on
              introductions. It captures the person, keeps their details live,
              stores the context of the meeting, and makes them findable later,
              so a handshake can become a second conversation.
            </p>
            <p className="vxm-body">
              A contactless card is one way to share in the room. You can start
              with a digital profile and add a card when you want one. The
              product is what happens after the exchange, not the metal.
            </p>
          </div>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className={`${styles.panel} vxm-reveal`}>
            <h2 className={styles.panelTitle}>Who it is for</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
              Consultants, founders, advisers, agents and anyone whose next
              piece of work comes from someone they have already met. And the
              sales, partnerships, recruitment and events teams whose network
              should belong to the business, not to a personal phone.
            </p>
            <div className="vxm-btn-row">
              <Link to="/professionals" className="vxm-btn vxm-btn--secondary">
                Velox for professionals
              </Link>
              <Link to="/enterprise" className="vxm-btn vxm-btn--secondary">
                Velox for teams
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTASection
        title="Start with the relationship, not the card"
        body="Create a profile today. Add a card when you want one, or tell us about a team."
        source="why_velox_final"
      />
    </div>
  );
};

export default WhyVeloxPage;
